import * as React from 'react';

import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';

import IDAForm from './Form';
import StartHistory from './HistoryRowData/Start';
import MonitoringHistory from './HistoryRowData/Monitoring';
import { fm } from 'Components/FormatMessage';
import { path } from 'Utility/ramdaReplacement';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';

const docType = (
  documents: Array<
    IDocumentWithTypeV2<
      'inflammatoryDiseaseActivityStart' | 'inflammatoryDiseaseActivityMonitoring' | 'relapse' | 'mri'
    >
  >,
  editing: string,
  view?: IView,
): string | undefined =>
  documents.length > 0 && editing
    ? (path(
        ['_type'],
        documents.find(d => d._id === editing),
      ) ?? undefined)
    : documents.length > 0 && view?.viewing
      ? (path(
          ['_type'],
          documents.find(d => d._id === view.viewing),
        ) ?? undefined)
      : undefined;

const InflammatoryDiseaseActivityHistory = (
  documents: Array<
    IDocumentWithTypeV2<
      'inflammatoryDiseaseActivityStart' | 'inflammatoryDiseaseActivityMonitoring' | 'relapse' | 'mri'
    >
  >,
  startEdit: StartEdit,
  view?: IView,
): React.JSX.Element => {
  const StartDocs = filterByDocumentType(documents, 'inflammatoryDiseaseActivityStart');
  const MonitorDocs = filterByDocumentType(documents, 'inflammatoryDiseaseActivityMonitoring');

  return (
    <React.Fragment>
      <StartHistory documents={StartDocs} startEdit={startEdit} view={view} />
      <MonitoringHistory documents={MonitorDocs} startEdit={startEdit} view={view} />
    </React.Fragment>
  );
};

const InflammatoryDiseaseActivity = ({ documents }: IOwnProps): React.JSX.Element => {
  const relapseDocs = filterByDocumentType(documents, 'relapse');
  const mriDocs = filterByDocumentType(documents, 'mri');

  return (
    <FormEditingHandler
      name="inflammatoryDiseaseActivity"
      documents={filterDocumentsByType(documents, [
        'inflammatoryDiseaseActivityStart',
        'inflammatoryDiseaseActivityMonitoring',
      ])}
    >
      {(editing, startEdit, formData, view): React.JSX.Element => {
        const editingDocument = editing && documents.find(d => d._id === editing);
        const viewingDocument = view?.viewing && documents.find(d => d._id === view.viewing);
        return (
          <DocumentWrapper>
            <DocumentHeader
              name={'inflammatoryDiseaseActivity'}
              header={
                <span>
                  {docType(documents, editing, view) === 'inflammatoryDiseaseActivityStart'
                    ? fm('inflammatoryDiseaseActivity.inflammatoryDiseaseActivityStart')
                    : docType(documents, editing, view) === 'inflammatoryDiseaseActivityMonitoring'
                      ? fm('inflammatoryDiseaseActivity.inflammatoryDiseaseActivityMonitoring')
                      : fm('inflammatoryDiseaseActivity.title')}
                  {docType(documents, editing, view) &&
                    (editing || view?.viewing) &&
                    (formData.document.date ?? formData.document.version) && (
                      <span>
                        &nbsp;
                        {'('}
                        {fm('inflammatoryDiseaseActivity.careGuidelines')}
                        &nbsp;
                        {formData.document.version === '2024' ? '2024' : '2020'}
                        {')'}
                      </span>
                    )}
                </span>
              }
              editing={editing}
            />
            {editingDocument ? (
              <IDAForm formData={formData} document={editingDocument} relapseDocs={relapseDocs} mriDocs={mriDocs} />
            ) : null}

            {viewingDocument ? (
              <IDAForm
                formData={{ document: view.document, onChange: (): string => '' }}
                document={viewingDocument}
                viewing={view.viewing}
                // intentionally empty (?)
                relapseDocs={[]}
                mriDocs={[]}
              />
            ) : null}

            {!editing && !view?.viewing ? InflammatoryDiseaseActivityHistory(documents, startEdit, view) : undefined}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<
    IDocumentWithTypeV2<
      'inflammatoryDiseaseActivityStart' | 'inflammatoryDiseaseActivityMonitoring' | 'relapse' | 'mri'
    >
  >;
}

export default InflammatoryDiseaseActivity;
